import { useRef, useState } from "react";
import { Box, Stack, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography, MenuItem, TextField } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PanToolIcon from "@mui/icons-material/PanTool";
import StraightenIcon from "@mui/icons-material/Straighten";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import type { DxPacsInstanceDto } from "@/api/diagnostics";

const W = 600, H = 420;
type Mode = "pan" | "measure" | "annotate";
type Pt = { x: number; y: number };
type Line = { a: Pt; b: Pt };
type Note = Pt & { text: string };

/** Lightweight image viewer (plan §5.11): zoom / pan / measure / annotate over a rendered PACS image. */
export function ImageViewer({ images, label }: { images: DxPacsInstanceDto[]; label?: string }) {
  const [idx, setIdx] = useState(0);
  const [scale, setScale] = useState(1); const [tx, setTx] = useState(0); const [ty, setTy] = useState(0);
  const [mode, setMode] = useState<Mode>("pan");
  const [lines, setLines] = useState<Line[]>([]); const [start, setStart] = useState<Pt | null>(null); const [notes, setNotes] = useState<Note[]>([]);
  const drag = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  const img = images[idx];
  const reset = () => { setScale(1); setTx(0); setTy(0); };
  const clearOverlays = () => { setLines([]); setNotes([]); setStart(null); };
  const zoom = (f: number) => setScale(s => Math.min(8, Math.max(0.25, +(s * f).toFixed(2))));

  const toLocal = (clientX: number, clientY: number): Pt => {
    const r = viewportRef.current!.getBoundingClientRect();
    return { x: (clientX - r.left - tx) / scale, y: (clientY - r.top - ty) / scale };
  };
  const dist = (a: Pt, b: Pt) => Math.round(Math.hypot(b.x - a.x, b.y - a.y));

  const onClick = (e: React.MouseEvent) => {
    if (mode === "pan") return;
    const p = toLocal(e.clientX, e.clientY);
    if (mode === "measure") {
      if (!start) setStart(p);
      else { setLines(l => [...l, { a: start, b: p }]); setStart(null); }
    } else if (mode === "annotate") {
      const text = window.prompt("Annotation");
      if (text) setNotes(n => [...n, { ...p, text }]);
    }
  };
  const onMouseDown = (e: React.MouseEvent) => { if (mode === "pan") drag.current = { x: e.clientX, y: e.clientY, tx, ty }; };
  const onMouseMove = (e: React.MouseEvent) => { if (drag.current) { setTx(drag.current.tx + (e.clientX - drag.current.x)); setTy(drag.current.ty + (e.clientY - drag.current.y)); } };
  const endDrag = () => { drag.current = null; };
  const onWheel = (e: React.WheelEvent) => { e.preventDefault(); zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15); };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
        {label && <Typography variant="caption" fontWeight={600}>{label}</Typography>}
        {images.length > 1 && (
          <TextField select size="small" value={idx} onChange={e => { setIdx(Number(e.target.value)); reset(); clearOverlays(); }} sx={{ minWidth: 140 }}>
            {images.map((im, i) => <MenuItem key={im.id} value={i}>#{im.instanceNumber ?? i + 1} {im.description ?? im.modality ?? ""}</MenuItem>)}
          </TextField>
        )}
        <Tooltip title="Zoom in"><IconButton size="small" onClick={() => zoom(1.2)}><ZoomInIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Zoom out"><IconButton size="small" onClick={() => zoom(1 / 1.2)}><ZoomOutIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Reset view"><IconButton size="small" onClick={reset}><RestartAltIcon fontSize="small" /></IconButton></Tooltip>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, m) => m && setMode(m)}>
          <ToggleButton value="pan"><Tooltip title="Pan"><PanToolIcon fontSize="small" /></Tooltip></ToggleButton>
          <ToggleButton value="measure"><Tooltip title="Measure"><StraightenIcon fontSize="small" /></Tooltip></ToggleButton>
          <ToggleButton value="annotate"><Tooltip title="Annotate"><EditIcon fontSize="small" /></Tooltip></ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Clear overlays"><IconButton size="small" onClick={clearOverlays}><ClearIcon fontSize="small" /></IconButton></Tooltip>
        <Typography variant="caption" color="text.secondary">{Math.round(scale * 100)}%</Typography>
      </Stack>

      <Box ref={viewportRef} onClick={onClick} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={endDrag} onMouseLeave={endDrag} onWheel={onWheel}
        sx={{ position: "relative", width: "100%", height: H, overflow: "hidden", border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "#000",
          cursor: mode === "pan" ? "grab" : "crosshair" }}>
        {!img && <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}><Typography variant="caption" color="grey.500">No images</Typography></Box>}
        {img && (
          <Box sx={{ position: "absolute", top: 0, left: 0, width: W, height: H, transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transformOrigin: "0 0" }}>
            <img src={img.imageUrl} alt={img.description ?? img.sopInstanceUid} width={W} height={H}
              style={{ objectFit: "contain", display: "block", pointerEvents: "none", userSelect: "none" }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.opacity = "0.15"; }} />
            <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
              {lines.map((l, i) => (
                <g key={i}>
                  <line x1={l.a.x} y1={l.a.y} x2={l.b.x} y2={l.b.y} stroke="#4fc3f7" strokeWidth={1.5} />
                  <text x={(l.a.x + l.b.x) / 2} y={(l.a.y + l.b.y) / 2 - 4} fill="#4fc3f7" fontSize={11}>{dist(l.a, l.b)} px</text>
                </g>
              ))}
              {start && <circle cx={start.x} cy={start.y} r={3} fill="#4fc3f7" />}
              {notes.map((n, i) => (
                <g key={i}>
                  <circle cx={n.x} cy={n.y} r={3} fill="#ffb300" />
                  <text x={n.x + 6} y={n.y + 3} fill="#ffb300" fontSize={11}>{n.text}</text>
                </g>
              ))}
            </svg>
          </Box>
        )}
      </Box>
    </Box>
  );
}
